import type { ImageView } from '@vizit/api';
import { Asset } from '@vizit/molecules';
import { AssetGrid } from '@vizit/organisms';
import * as React from 'react';
import { useAssetGridViewer } from './asset-grid-viewer.hooks';

interface AssetGridViewerProps {
  /**
   * Additional CSS classes to apply to the grid container
   * @type {string}
   * @optional
   */
  className?: string;
  /**
   * Upload asset dialog to display in the empty state
   * @type {React.ReactNode}
   * @optional
   */
  uploadAssetDialog?: React.ReactNode;
  /**
   * Callback when an asset is clicked
   * @type {(id: string) => void}
   * @required
   */
  onNavigateToAsset: (id: string) => void;
  /**
   * Whether to automatically calculate optimal initial load count
   * @default true
   */
  useOptimalInitialLoad?: boolean;
  /**
   * Fixed images per row, e.g. from the thumbnail size slider. `undefined` keeps the responsive columns
   * @optional
   */
  columns?: number;
}

/**
 * AssetGridViewer component that fetches and displays portfolio assets
 * Uses the dev API for testing purposes
 *
 * @param props - The component props
 * @param props.className - Additional CSS classes
 */
const AssetGridViewer = React.memo(function AssetGridViewer(props: AssetGridViewerProps) {
  const { className, uploadAssetDialog, onNavigateToAsset, useOptimalInitialLoad, columns } = props;

  const { assets, totalCount, isLoading, hasNextPage, isFetchingNextPage, sentinelRef, loadingCount } =
    useAssetGridViewer({
      useOptimalInitialLoad,
      columns,
    });

  return (
    <>
      <AssetGrid
        loadingCount={loadingCount}
        totalCount={totalCount}
        isLoading={isLoading}
        className={className}
        uploadAssetDialog={uploadAssetDialog}
        columns={columns}
      >
        {assets.map((asset: ImageView) => (
          <Asset
            onClick={() => {
              onNavigateToAsset(asset.id);
            }}
            onPress={() => {
              onNavigateToAsset(asset.id);
            }}
            key={asset.id}
            showScoreBar={false}
            isLoading={false}
          >
            <Asset.Image name={asset.name} src={asset.url_360 ?? asset.url} fallbackSrc={asset.url} />
          </Asset>
        ))}

        {/* Show loading assets when fetching next page */}
        {isFetchingNextPage && loadingCount
          ? Array.from({ length: loadingCount }, (_, index) => {
              const skeletonId = `asset-skeleton-next-${index}`;
              return (
                <Asset key={skeletonId} isLoading showScoreBar={false}>
                  <Asset.Image name="Loading..." src={null} />
                </Asset>
              );
            })
          : null}
      </AssetGrid>

      {/* Sentinel element for infinite scroll detection */}
      {hasNextPage && <div ref={sentinelRef} className="h-4 w-full" aria-label="Loading more assets" />}
    </>
  );
});

export { AssetGridViewer };
export type { AssetGridViewerProps };
